import React, { useMemo, useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import {
  Alert,
  DatePicker,
  Dropdown,
  Empty,
  Input,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { ColumnsType } from "antd/es/table"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import SegmentWrapper from "components/custom/SegmentWrapper"
import { Button } from "components"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { useAppNotificationStore } from "stores"
import { onQueryError } from "utils/utils"
import PendingLeaveReport from "./PendingLeaveReport"
import FeatureHint from "components/new-stuff/ui/FeatureHint"
import { requestsService, REQUESTS_ENDPOINTS } from "services/requestsService"
import {
  leaveService,
  LEAVE_ENDPOINTS,
  IApprovalHistoryRow,
  ILeaveByDivisionRow,
  ILeaveDepartmentOption,
  ILeaveByTypeRow,
  ILeaveReportFilter,
  ILeaveTakenRow,
  ISavedLeaveReportView,
} from "services/leaveService"

const ISO = "YYYY-MM-DD"
const API = "DD/MM/YYYY"

type ReportKey = "pending" | "employee" | "type" | "division" | "history"

interface IPolicyOption {
  leave_policy_id: number
  description: string
}

/**
 * RP. Leave analytics: days taken by employee, by type, by department, and the decisions taken
 * on each request.
 *
 * The four sit together because they answer the same question at different grain and are read
 * against each other — total days must agree across the first three, so they share one date
 * range rather than each keeping its own. Only the open tab queries: each report scans a
 * period's requests, and loading four at once to show one would be paid for on every visit.
 */
const LeaveAnalytics: React.FC<{ isActive?: boolean }> = ({
  isActive = true,
}) => {
  // Pending leads: it is the only one of the five that is about something needing action
  // today rather than about what already happened.
  const [tab, setTab] = useState<ReportKey>("pending")
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("year"),
    dayjs().endOf("year"),
  ])
  const [departmentId, setDepartmentId] = useState<number | undefined>()
  const [leavePolicyId, setLeavePolicyId] = useState<number | undefined>()

  // Kept per report rather than globally: hiding "Unpaid" on the employee report says nothing
  // about whether you want "Busiest day" on the department one.
  const [hiddenColumns, setHiddenColumns] = useState<Record<ReportKey, string[]>>({
    pending: [],
    employee: [],
    type: [],
    division: [],
    history: [],
  })

  const toggleColumn = (report: ReportKey) => (title: string) =>
    setHiddenColumns((current) => {
      const hidden = current[report]
      return {
        ...current,
        [report]: hidden.includes(title)
          ? hidden.filter((name) => name !== title)
          : [...hidden, title],
      }
    })

  const filter: ILeaveReportFilter = useMemo(
    () => ({
      first_day: range[0].format(API),
      last_day: range[1].format(API),
      department_id: departmentId,
      leave_policy_id: leavePolicyId,
    }),
    [range, departmentId, leavePolicyId]
  )

  const { toast } = useAppNotificationStore()
  const queryClient = useQueryClient()
  const [savingView, setSavingView] = useState(false)
  const [viewName, setViewName] = useState("")

  const { data: savedViewsRes } = useQuery({
    queryKey: [LEAVE_ENDPOINTS.VIEW_LEAVE_REPORT_VIEWS],
    queryFn: () => leaveService.getSavedReportViews(),
    enabled: isActive,
    staleTime: 0,
  })

  const savedViews: ISavedLeaveReportView[] =
    (savedViewsRes as { data?: ISavedLeaveReportView[] } | undefined)?.data ?? []

  const refreshViews = () =>
    queryClient.invalidateQueries({
      queryKey: [LEAVE_ENDPOINTS.VIEW_LEAVE_REPORT_VIEWS],
    })

  const saveView = useMutation({
    mutationFn: () =>
      leaveService.saveReportView({
        report_key: tab,
        description: viewName.trim(),
        // Everything needed to put the screen back exactly as it is now. Written and read by
        // this screen alone, which is why it is a blob rather than columns in a table.
        definition: JSON.stringify({
          first_day: range[0].format(ISO),
          last_day: range[1].format(ISO),
          department_id: departmentId ?? null,
          leave_policy_id: leavePolicyId ?? null,
          hidden_columns: hiddenColumns[tab],
        }),
      }),
    onSuccess: (res: any) => {
      toast.success(res?.message || "View saved.")
      setSavingView(false)
      setViewName("")
      refreshViews()
    },
    onError: onQueryError,
  })

  const deleteView = useMutation({
    mutationFn: (id: number) => leaveService.deleteReportView(id),
    onSuccess: (res: any) => {
      toast.success(res?.message || "Saved view removed.")
      refreshViews()
    },
    onError: onQueryError,
  })

  const applyView = (view: ISavedLeaveReportView) => {
    if (!view.definition) return

    try {
      const saved = JSON.parse(view.definition)

      if (saved.first_day && saved.last_day) {
        setRange([dayjs(saved.first_day, ISO), dayjs(saved.last_day, ISO)])
      }
      setDepartmentId(saved.department_id ?? undefined)
      setLeavePolicyId(saved.leave_policy_id ?? undefined)

      if (view.report_key) {
        setTab(view.report_key as ReportKey)
        setHiddenColumns((current) => ({
          ...current,
          [view.report_key as ReportKey]: saved.hidden_columns ?? [],
        }))
      }
    } catch {
      // A view saved by an older version of this screen, or hand-edited. Better to say so than
      // to apply half of it and leave the reader wondering which half.
      toast.error("That saved view could not be read.")
    }
  }

  const viewsForTab = savedViews.filter((view) => view.report_key === tab)

  const { data: departmentsRes } = useQuery({
    queryKey: [LEAVE_ENDPOINTS.VIEW_LEAVE_DEPARTMENTS],
    queryFn: () => leaveService.getDepartmentOptions(),
    enabled: isActive,
  })

  const { data: policiesRes } = useQuery({
    queryKey: [REQUESTS_ENDPOINTS.ADMIN_LEAVE_POLICIES],
    queryFn: () => requestsService.adminGetLeavePolicies(),
    enabled: isActive,
  })

  const departments: ILeaveDepartmentOption[] =
    (departmentsRes as { data?: ILeaveDepartmentOption[] } | undefined)?.data ?? []

  const policies: IPolicyOption[] =
    (policiesRes as { data?: IPolicyOption[] } | undefined)?.data ?? []

  return (
    <SegmentWrapper title="Leave reports">
      <div className="px-4 md:px-6 lg:px-8 xl:px-[60px] pb-6 flex flex-col gap-4">
        <div
          className={
            tab === "pending"
              ? "hidden"
              : "flex flex-wrap items-center gap-3"
          }
        >
          <DatePicker.RangePicker
            value={range}
            allowClear={false}
            format={ISO}
            onChange={(value) => {
              if (value?.[0] && value?.[1]) {
                setRange([value[0], value[1]])
              }
            }}
          />

          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All departments"
            className="min-w-[190px]"
            value={departmentId}
            onChange={setDepartmentId}
            options={departments.map((d) => ({
              label: d.description,
              value: d.departmentId,
            }))}
          />

          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All leave types"
            className="min-w-[190px]"
            value={leavePolicyId}
            onChange={setLeavePolicyId}
            options={policies.map((p) => ({
              label: p.description,
              value: p.leave_policy_id,
            }))}
          />

          <div className="flex items-center gap-2 ml-auto">
            {viewsForTab.length > 0 ? (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: viewsForTab.map((view) => ({
                    key: String(view.leave_report_view_id),
                    label: (
                      <div className="flex items-center justify-between gap-4 min-w-[200px]">
                        <span className="font-avenir text-sm">
                          {view.description}
                        </span>
                        <span
                          className="font-avenir text-xs text-padeSubtext hover:text-[#A33B22]"
                          onClick={(event) => {
                            event.stopPropagation()
                            deleteView.mutate(view.leave_report_view_id)
                          }}
                        >
                          Remove
                        </span>
                      </div>
                    ),
                  })),
                  onClick: ({ key }) => {
                    const view = viewsForTab.find(
                      (candidate) => String(candidate.leave_report_view_id) === key
                    )
                    if (view) applyView(view)
                  },
                }}
              >
                <Button color="neutral">Saved views ({viewsForTab.length})</Button>
              </Dropdown>
            ) : null}

            <Tooltip title="Keeps this report's date range, filters and visible columns under a name you choose. Private to you.">
              <Button
                color="neutral"
                onClick={() => setSavingView(true)}
              >
                Save this view
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* Saving is per report: the range and filters are shared across tabs, but which
            columns matter is not, so a view restores the tab it was saved from. */}
        {/* The app's own buttons rather than antd's default footer: the global stylesheet
            resets button backgrounds and only restores antd's primary fill under a class name
            antd generates in local development, so its OK button rendered transparent. */}
        <CustomModal
          open={savingView}
          title="Save this view"
          onCancel={() => {
            setSavingView(false)
            setViewName("")
          }}
          footer={
            <div className="w-full flex justify-end gap-3">
              <Button
                color="neutral"
                onClick={() => {
                  setSavingView(false)
                  setViewName("")
                }}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={!viewName.trim() || saveView.isLoading}
                submitting={saveView.isLoading}
                onClick={() => saveView.mutate()}
              >
                Save
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-2">
            <span className="font-avenir text-sm text-padeSubtext">
              Saves the date range, the filters and the columns you are showing, for this report
              only. Saving over a name you have used before replaces it.
            </span>
            <Input
              autoFocus
              placeholder="For example: Q1 by department, no rates"
              value={viewName}
              onChange={(event) => setViewName(event.target.value)}
              onPressEnter={() => {
                if (viewName.trim()) saveView.mutate()
              }}
            />
          </div>
        </CustomModal>

        {/* Leave is attributed to the day it starts, so a request running over the boundary
            is counted whole in the period it began. Said here because the alternative —
            splitting it — would stop these totals matching an employee's own balance. Not shown
            on the pending tab, which reports what is open now rather than a period. */}
        {tab === "pending" ? null : (
          <p className="font-avenir text-xs text-padeSubtext m-0">
            A request counts in the period it starts in, and is never split across two periods.
          </p>
        )}

        <Tabs
          activeKey={tab}
          onChange={(key) => setTab(key as ReportKey)}
          items={[
            {
              key: "pending",
              label: (
                <span className="flex items-center gap-1.5">
                  Awaiting decision
                  <FeatureHint>
                    Leave that has been requested and not yet approved or declined, oldest first,
                    with who it is sitting with. There is no automatic escalation, so this is
                    where a backlog becomes visible.
                  </FeatureHint>
                </span>
              ),
              children:
                isActive && tab === "pending" ? <PendingLeaveReport embedded /> : null,
            },
            {
              key: "employee",
              label: (
                <span className="flex items-center gap-1.5">
                  By employee
                  <FeatureHint>
                    Days taken per person over the range, split into paid and unpaid. Unpaid days
                    come from leave types marked as unpaid on the policy.
                  </FeatureHint>
                </span>
              ),
              children: (
                <ByEmployee
                  filter={filter}
                  enabled={isActive && tab === "employee"}
                  hiddenColumns={hiddenColumns.employee}
                  onToggleColumn={toggleColumn("employee")}
                />
              ),
            },
            {
              key: "type",
              label: "By leave type",
              children: (
                <ByType
                  filter={filter}
                  enabled={isActive && tab === "type"}
                  hiddenColumns={hiddenColumns.type}
                  onToggleColumn={toggleColumn("type")}
                />
              ),
            },
            {
              key: "division",
              label: (
                <span className="flex items-center gap-1.5">
                  By department
                  <FeatureHint>
                    Days and requests per department, with the worst day for people being away at
                    once. The absence rate is measured against the people who took leave, not
                    total headcount — the two are easily confused.
                  </FeatureHint>
                </span>
              ),
              children: (
                <ByDivision
                  filter={filter}
                  enabled={isActive && tab === "division"}
                  hiddenColumns={hiddenColumns.division}
                  onToggleColumn={toggleColumn("division")}
                />
              ),
            },
            {
              key: "history",
              label: (
                <span className="flex items-center gap-1.5">
                  Approval history
                  <FeatureHint>
                    Who decided what, when, and any comment they left. Expand a row to see the
                    steps. It shows the decisions actually recorded, so editing a workflow later
                    does not rewrite what happened.
                  </FeatureHint>
                </span>
              ),
              children: (
                <ApprovalHistory
                  filter={filter}
                  enabled={isActive && tab === "history"}
                  hiddenColumns={hiddenColumns.history}
                  onToggleColumn={toggleColumn("history")}
                />
              ),
            },
          ]}
        />
      </div>
    </SegmentWrapper>
  )
}

interface TabProps {
  filter: ILeaveReportFilter
  enabled: boolean
  /** Column titles the viewer has switched off; identity is the title, which is unique per table. */
  hiddenColumns: string[]
  onToggleColumn: (title: string) => void
}

/** One number and its label. Kept flat so a row of them reads as a summary, not four cards. */
const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col">
    <span className="font-avenir text-xs text-padeSubtext">{label}</span>
    <span className="font-avenir text-lg font-semibold text-padeBlack tabular-nums">
      {value}
    </span>
  </div>
)

/**
 * Shared frame: the states every report has, so a slow query, a failed one and an empty
 * period look the same whichever tab you are on.
 */
const ReportFrame: React.FC<{
  isError: boolean
  isEmpty: boolean
  stats?: React.ReactNode
  onExport?: () => void
  /** Every column this report can show, in table order. */
  columnTitles?: string[]
  hiddenColumns?: string[]
  onToggleColumn?: (title: string) => void
  children: React.ReactNode
}> = ({
  isError,
  isEmpty,
  stats,
  onExport,
  columnTitles,
  hiddenColumns = [],
  onToggleColumn,
  children,
}) => {
  if (isError) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Could not load this report"
        description="Try reloading the page."
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {stats ? (
        <div className="flex flex-wrap items-start gap-8">{stats}</div>
      ) : null}

      {isEmpty ? (
        <Empty description="No leave was taken in this period" />
      ) : (
        <>
          <div className="flex justify-end gap-2">
            {columnTitles?.length && onToggleColumn ? (
              <Dropdown
                trigger={["click"]}
                menu={{
                  // Checkable rather than a multi-select: the list is short, and toggling one
                  // column at a time is what people actually do with it.
                  items: columnTitles.map((title) => ({
                    key: title,
                    label: (
                      <span className="font-avenir text-sm">
                        {hiddenColumns.includes(title) ? "☐" : "☑"} {title}
                      </span>
                    ),
                  })),
                  onClick: ({ key }) => onToggleColumn(String(key)),
                }}
              >
                <Tooltip title="Choose which columns to show. The export follows exactly what is on screen.">
                  <Button color="neutral">
                    Columns
                    {hiddenColumns.length > 0 ? ` (${hiddenColumns.length} hidden)` : ""}
                  </Button>
                </Tooltip>
              </Dropdown>
            ) : null}
            {onExport ? (
              <Tooltip title="Exports exactly the columns shown">
                <Button
                  color="secondary"
                  onClick={onExport}
                >
                  Export
                </Button>
              </Tooltip>
            ) : null}
          </div>
          {children}
        </>
      )}
    </div>
  )
}

/**
 * Writes the rows on screen, with the headings on screen. Exporting a different shape from
 * what was displayed is how an export stops being trusted.
 */
const exportRows = (
  fileName: string,
  columns: { header: string; key: string }[],
  rows: Record<string, unknown>[],
  hiddenColumns: string[] = []
) => {
  // Hidden on screen means absent from the sheet. The alternative — exporting everything
  // regardless — makes the column picker a lie the moment anyone opens the file.
  const shown = columns.filter((column) => !hiddenColumns.includes(column.header))

  const sheet = XLSX.utils.json_to_sheet(
    rows.map((row) =>
      shown.reduce<Record<string, unknown>>((out, column) => {
        out[column.header] = row[column.key] ?? ""
        return out
      }, {})
    ),
    { header: shown.map((column) => column.header) }
  )

  const book = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(book, sheet, "Report")
  saveAs(
    new Blob([XLSX.write(book, { bookType: "xlsx", type: "array" })], {
      type: "application/octet-stream",
    }),
    `${fileName}.xlsx`
  )
}

const ByEmployee: React.FC<TabProps> = ({
  filter,
  enabled,
  hiddenColumns,
  onToggleColumn,
}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: [LEAVE_ENDPOINTS.VIEW_LEAVE_TAKEN_REPORT, filter],
    queryFn: () => leaveService.getLeaveTakenReport(filter),
    enabled,
    staleTime: 0,
  })

  const report = data?.data
  const rows = report?.rows ?? []

  // Declared outside the table so the picker and the export can both read the same
  // list: an export that quietly differs from what is on screen is how an export stops
  // being trusted.
  const allColumns: ColumnsType<ILeaveTakenRow> = [
          { title: "Employee", render: (_, r) => r.full_name ?? "—" },
          { title: "Department", render: (_, r) => r.department ?? "—" },
          {
            title: "Requests",
            dataIndex: "requests",
            sorter: (a, b) => a.requests - b.requests,
          },
          {
            title: "Days taken",
            dataIndex: "days_taken",
            defaultSortOrder: "descend",
            sorter: (a, b) => a.days_taken - b.days_taken,
          },
          { title: "Paid", dataIndex: "paid_days" },
          {
            title: "Unpaid",
            render: (_, r) =>
              r.unpaid_days > 0 ? (
                <Tag color="orange">{r.unpaid_days}</Tag>
              ) : (
                r.unpaid_days
              ),
          },
        ]

  const visibleColumns = allColumns.filter(
    (column) => !hiddenColumns.includes(String(column.title))
  )

  return (
    <ReportFrame
      isError={isError}
      isEmpty={!isLoading && rows.length === 0}
      columnTitles={allColumns.map((column) => String(column.title))}
      hiddenColumns={hiddenColumns}
      onToggleColumn={onToggleColumn}
      stats={
        <>
          <Stat
            label="Days taken"
            value={report?.total_days ?? "—"}
          />
          <Stat
            label="Paid"
            value={report?.total_paid_days ?? "—"}
          />
          <Stat
            label="Unpaid"
            value={report?.total_unpaid_days ?? "—"}
          />
          <Stat
            label="Employees"
            value={rows.length}
          />
        </>
      }
      onExport={() =>
        exportRows(
          "leave_by_employee",
          [
            { header: "Employee", key: "full_name" },
            { header: "Department", key: "department" },
            { header: "Requests", key: "requests" },
            { header: "Days taken", key: "days_taken" },
            { header: "Paid days", key: "paid_days" },
            { header: "Unpaid days", key: "unpaid_days" },
          ],
          rows as unknown as Record<string, unknown>[],
          hiddenColumns
        )
      }
    >
      <Table<ILeaveTakenRow>
        loading={isLoading}
        dataSource={rows}
        rowKey={(row) => `${row.person_id}`}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        scroll={{ x: true }}
        columns={visibleColumns as never}
      />
    </ReportFrame>
  )
}

const ByType: React.FC<TabProps> = ({
  filter,
  enabled,
  hiddenColumns,
  onToggleColumn,
}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: [LEAVE_ENDPOINTS.VIEW_LEAVE_BY_TYPE_REPORT, filter],
    queryFn: () => leaveService.getLeaveByTypeReport(filter),
    enabled,
    staleTime: 0,
  })

  const report = data?.data
  const rows = report?.rows ?? []

  // Declared outside the table so the picker and the export can both read the same
  // list: an export that quietly differs from what is on screen is how an export stops
  // being trusted.
  const allColumns: ColumnsType<ILeaveByTypeRow> = [
          {
            title: "Leave type",
            render: (_, r) => (
              <div className="flex items-center gap-2">
                <span>{r.leave_policy ?? "—"}</span>
                {r.is_paid === false ? <Tag>Unpaid</Tag> : null}
              </div>
            ),
          },
          {
            title: "Requests",
            dataIndex: "requests",
            sorter: (a, b) => a.requests - b.requests,
          },
          { title: "Employees", dataIndex: "employees" },
          {
            title: "Days taken",
            dataIndex: "days_taken",
            defaultSortOrder: "descend",
            sorter: (a, b) => a.days_taken - b.days_taken,
          },
          { title: "Average length", dataIndex: "average_duration" },
        ]

  const visibleColumns = allColumns.filter(
    (column) => !hiddenColumns.includes(String(column.title))
  )

  return (
    <ReportFrame
      isError={isError}
      isEmpty={!isLoading && rows.length === 0}
      columnTitles={allColumns.map((column) => String(column.title))}
      hiddenColumns={hiddenColumns}
      onToggleColumn={onToggleColumn}
      stats={
        <>
          <Stat
            label="Days taken"
            value={report?.total_days ?? "—"}
          />
          <Stat
            label="Leave types used"
            value={rows.length}
          />
        </>
      }
      onExport={() =>
        exportRows(
          "leave_by_type",
          [
            { header: "Leave type", key: "leave_policy" },
            { header: "Requests", key: "requests" },
            { header: "Employees", key: "employees" },
            { header: "Days taken", key: "days_taken" },
            { header: "Average days per request", key: "average_duration" },
          ],
          rows as unknown as Record<string, unknown>[],
          hiddenColumns
        )
      }
    >
      <Table<ILeaveByTypeRow>
        loading={isLoading}
        dataSource={rows}
        rowKey={(row) => `${row.leave_policy_id}`}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        scroll={{ x: true }}
        columns={visibleColumns as never}
      />
    </ReportFrame>
  )
}

const ByDivision: React.FC<TabProps> = ({
  filter,
  enabled,
  hiddenColumns,
  onToggleColumn,
}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: [LEAVE_ENDPOINTS.VIEW_LEAVE_BY_DIVISION_REPORT, filter],
    queryFn: () => leaveService.getLeaveByDivisionReport(filter),
    enabled,
    staleTime: 0,
  })

  const report = data?.data
  const rows = report?.rows ?? []

  // Declared outside the table so the picker and the export can both read the same
  // list: an export that quietly differs from what is on screen is how an export stops
  // being trusted.
  const allColumns: ColumnsType<ILeaveByDivisionRow> = [
          { title: "Department", render: (_, r) => r.department ?? "—" },
          { title: "Employees away", dataIndex: "employees_absent" },
          { title: "Requests", dataIndex: "requests" },
          {
            title: "Days taken",
            dataIndex: "days_taken",
            defaultSortOrder: "descend",
            sorter: (a, b) => a.days_taken - b.days_taken,
          },
          {
            title: "Most away at once",
            render: (_, r) => (
              <div className="flex flex-col">
                <span className="tabular-nums">
                  {r.peak_concurrent_absence}
                </span>
                {r.peak_day ? (
                  <span className="font-avenir text-xs text-padeSubtext">
                    on {r.peak_day}
                  </span>
                ) : null}
              </div>
            ),
          },
          {
            // Named for what it measures: the rate is over the people who took leave, not
            // over headcount, and calling it "absence rate" alone invites the wrong reading.
            title: "Absence rate",
            render: (_, r) => `${r.absence_rate}%`,
            sorter: (a, b) => a.absence_rate - b.absence_rate,
          },
        ]

  const visibleColumns = allColumns.filter(
    (column) => !hiddenColumns.includes(String(column.title))
  )

  return (
    <ReportFrame
      isError={isError}
      isEmpty={!isLoading && rows.length === 0}
      columnTitles={allColumns.map((column) => String(column.title))}
      hiddenColumns={hiddenColumns}
      onToggleColumn={onToggleColumn}
      stats={
        <>
          <Stat
            label="Days taken"
            value={report?.total_days ?? "—"}
          />
          <Stat
            label="Departments"
            value={rows.length}
          />
        </>
      }
      onExport={() =>
        exportRows(
          "leave_by_division",
          [
            { header: "Department", key: "department" },
            { header: "Employees away", key: "employees_absent" },
            { header: "Requests", key: "requests" },
            { header: "Days taken", key: "days_taken" },
            { header: "Most away on one day", key: "peak_concurrent_absence" },
            { header: "Busiest day", key: "peak_day" },
            { header: "Absence rate %", key: "absence_rate" },
          ],
          rows as unknown as Record<string, unknown>[],
          hiddenColumns
        )
      }
    >
      <Table<ILeaveByDivisionRow>
        loading={isLoading}
        dataSource={rows}
        rowKey={(row) => `${row.department_id}`}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        scroll={{ x: true }}
        columns={visibleColumns as never}
      />
    </ReportFrame>
  )
}

const DECISION_COLOURS: Record<string, string> = {
  Approve: "green",
  Decline: "red",
}

const ApprovalHistory: React.FC<TabProps> = ({
  filter,
  enabled,
  hiddenColumns,
  onToggleColumn,
}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: [LEAVE_ENDPOINTS.VIEW_LEAVE_APPROVAL_HISTORY_REPORT, filter],
    queryFn: () => leaveService.getLeaveApprovalHistoryReport(filter),
    enabled,
    staleTime: 0,
  })

  const report = data?.data
  const rows = report?.rows ?? []

  // Declared outside the table so the picker and the export can both read the same
  // list: an export that quietly differs from what is on screen is how an export stops
  // being trusted.
  const allColumns: ColumnsType<IApprovalHistoryRow> = [
          {
            title: "Employee",
            render: (_, r) => (
              <div className="flex flex-col">
                <span>{r.full_name ?? "—"}</span>
                <span className="font-avenir text-xs text-padeSubtext">
                  {r.department ?? "—"}
                </span>
              </div>
            ),
          },
          {
            title: "Leave",
            render: (_, r) => (
              <div className="flex flex-col">
                <span>{r.leave_policy ?? "—"}</span>
                <span className="font-avenir text-xs text-padeSubtext">
                  {r.first_day} – {r.last_day}
                  {r.days != null ? ` · ${r.days} days` : ""}
                </span>
              </div>
            ),
          },
          { title: "Status", render: (_, r) => r.status ?? "—" },
          { title: "Submitted", render: (_, r) => r.submitted_on ?? "—" },
          {
            title: "Decisions",
            render: (_, r) =>
              r.steps_recorded === 0 ? (
                <Tag color="orange">None yet</Tag>
              ) : (
                // The configured chain length is unknown for some requests, so it is shown
                // only where the workflow can say what it is.
                <span className="tabular-nums">
                  {r.workflow_steps != null
                    ? `${r.steps_recorded} of ${r.workflow_steps}`
                    : r.steps_recorded}
                </span>
              ),
          },
          {
            title: "Days to decide",
            dataIndex: "days_to_decide",
            defaultSortOrder: "descend",
            sorter: (a, b) => a.days_to_decide - b.days_to_decide,
            render: (_, r) =>
              r.steps_recorded === 0 ? (
                <span className="tabular-nums">{r.days_to_decide} so far</span>
              ) : (
                <span className="tabular-nums">{r.days_to_decide}</span>
              ),
          },
        ]

  const visibleColumns = allColumns.filter(
    (column) => !hiddenColumns.includes(String(column.title))
  )

  return (
    <ReportFrame
      isError={isError}
      isEmpty={!isLoading && rows.length === 0}
      columnTitles={allColumns.map((column) => String(column.title))}
      hiddenColumns={hiddenColumns}
      onToggleColumn={onToggleColumn}
      stats={
        <>
          <Stat
            label="Requests"
            value={report?.total_requests ?? "—"}
          />
          <Stat
            label="No decision recorded"
            value={report?.undecided_requests ?? "—"}
          />
        </>
      }
      onExport={() =>
        exportRows(
          "leave_approval_history",
          [
            { header: "Request", key: "employee_request_id" },
            { header: "Employee", key: "full_name" },
            { header: "Department", key: "department" },
            { header: "Leave type", key: "leave_policy" },
            { header: "First day", key: "first_day" },
            { header: "Last day", key: "last_day" },
            { header: "Days", key: "days" },
            { header: "Status", key: "status" },
            { header: "Submitted", key: "submitted_on" },
            { header: "Decisions recorded", key: "steps_recorded" },
            { header: "Days to decide", key: "days_to_decide" },
            { header: "Decisions", key: "decisions" },
          ],
          rows.map((row) => ({
            ...row,
            // Flattened for the sheet: one row per request keeps it readable, and the
            // decisions belong with the request rather than in a second file.
            decisions: row.steps
              .map(
                (step) =>
                  `${step.position}. ${step.decision} — ${
                    step.decided_by ?? "unknown"
                  }${step.decided_on ? ` on ${step.decided_on}` : ""}`
              )
              .join(" | "),
          })) as unknown as Record<string, unknown>[],
          hiddenColumns
        )
      }
    >
      <Table<IApprovalHistoryRow>
        loading={isLoading}
        dataSource={rows}
        rowKey={(row) => `${row.employee_request_id}`}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        scroll={{ x: true }}
        expandable={{
          // The decisions are the point of the report, but most rows have two or three, so
          // they open rather than widening every row into a paragraph.
          expandedRowRender: (row) =>
            row.steps.length === 0 ? (
              <span className="font-avenir text-xs text-padeSubtext">
                Nobody has acted on this request yet.
              </span>
            ) : (
              <ol className="flex flex-col gap-1 m-0 pl-4">
                {row.steps.map((step) => (
                  <li
                    key={step.position}
                    className="font-avenir text-xs text-padeBlack"
                  >
                    <Tag color={DECISION_COLOURS[step.decision ?? ""]}>
                      {step.decision}
                    </Tag>
                    {step.decided_by ?? "Unknown"}
                    {step.decided_on ? ` · ${step.decided_on}` : ""}
                    {step.comment ? ` · “${step.comment}”` : ""}
                  </li>
                ))}
              </ol>
            ),
        }}
        columns={visibleColumns as never}
      />
    </ReportFrame>
  )
}

export default LeaveAnalytics
